import {useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import {WorkforceDashboardDepartmentHealthRow} from './WorkforceDashboardDepartmentHealthRow';
import type {WorkforceDashboardDepartmentHealthTableRow} from './WorkforceDashboardDepartmentHealthRow';
import {WORKFORCE_DASHBOARD_SCROLLABLE_SECTION_MAX_HEIGHT} from './workforceDashboardLayoutConstants';
import {WorkforceDashboardSectionCard} from './WorkforceDashboardSectionCard';
import {BasicTable} from '../../../../../components/BasicTable/BasicTable';
import {createBasicTableColumn} from '../../../../../components/BasicTable/createBasicTableColumn';
import {EmptyStateCard} from '../../../../../components/EmptyStateCard/EmptyStateCard';
import type {WorkforceDashboardDepartmentHealth as WorkforceDashboardDepartmentHealthData} from '../../types/workforceDashboardTypes';
import {sortDepartmentHealthByRisk} from '../../utils/WorkforceDashboard/sortDepartmentHealthByRisk';

export interface Props {
  data: WorkforceDashboardDepartmentHealthData[] | null;
}

export const WorkforceDashboardDepartmentHealth = ({data}: Props) => {
  const {t} = useTranslation('translation', {keyPrefix: 'workforceDashboardPage.sections.departmentHealth'});

  const columns = useMemo(
    () => [
      createBasicTableColumn<WorkforceDashboardDepartmentHealthTableRow>('department', t('columns.department'), {
        sortable: true,
        getSortValue: (row) => row.companyDepartmentName,
      }),
      createBasicTableColumn<WorkforceDashboardDepartmentHealthTableRow>('summary', t('columns.summary'), {
        sortable: true,
        getSortValue: (row) => row.gapCount,
      }),
      createBasicTableColumn<WorkforceDashboardDepartmentHealthTableRow>('healthBar', t('columns.healthBar'), {
        sx: {width: '35%'},
      }),
      createBasicTableColumn<WorkforceDashboardDepartmentHealthTableRow>('healthScore', t('columns.healthScore'), {
        align: 'right',
        sortable: true,
        getSortValue: (row) => row.averageAlignmentScore,
      }),
    ],
    [t]
  );

  const rowData = useMemo<WorkforceDashboardDepartmentHealthTableRow[]>(
    () =>
      sortDepartmentHealthByRisk(data ?? []).map((item) => ({
        ...item,
        id: item.companyDepartmentId,
      })),
    [data]
  );

  if (!data || data.length === 0) {
    return (
      <WorkforceDashboardSectionCard title={t('title')}>
        <EmptyStateCard />
      </WorkforceDashboardSectionCard>
    );
  }

  return (
    <WorkforceDashboardSectionCard title={t('title')}>
      <BasicTable
        columns={columns}
        rowData={rowData}
        Row={WorkforceDashboardDepartmentHealthRow}
        hideLoadMoreFooter
        tableContainerSx={{
          maxHeight: WORKFORCE_DASHBOARD_SCROLLABLE_SECTION_MAX_HEIGHT,
          overflowY: 'auto',
        }}
      />
    </WorkforceDashboardSectionCard>
  );
};
